import React from "react";
import { Phone, Mail, MapPin, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO_URL, CONTACT, SLOGAN } from "@/lib/company";

const NAV_MAIN = [
  { label: "Inicio", href: "#top" },
  { label: "Servicios", href: "#servicios" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Proceso", href: "#proceso" },
  { label: "FAQ", href: "#faq" },
  { label: "Contacto", href: "#contacto" },
];

const NAV_LEGAL = [
  { label: "Blog", href: "/blog", link: true },
  { label: "Recursos gratis", href: "#recursos" },
  { label: "Política de Privacidad", href: "/legal/privacidad", link: true },
  { label: "Términos y Condiciones", href: "/legal/terminos", link: true },
];

export default function Footer() {
  const scrollTo = (h) => document.querySelector(h)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className="bg-[#0f172a] text-white pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="mb-5 bg-white/95 p-3 rounded-sm inline-block">
              <img src={LOGO_URL} alt="Great Solutions US" className="h-16 w-auto" />
            </div>
            <div className="text-sm font-semibold text-[#C89B2C] italic mb-3">
              {SLOGAN}
            </div>
            <p className="text-white/70 text-[15px] leading-relaxed max-w-md">
              Soluciones tributarias, empresariales, notariales, migratorias y de seguros en Florida. Servicio
              profesional, transparente y personalizado para personas, familias y pequeños negocios.
            </p>
            <a
              href={CONTACT.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#C89B2C] transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={16} />
              @{CONTACT.instagram}
            </a>
          </div>

          {/* Nav */}
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold text-[#C89B2C] tracking-[0.2em] uppercase mb-5">
              Navegación
            </div>
            <ul className="space-y-3">
              {NAV_MAIN.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scrollTo(l.href)}
                    className="text-white/70 hover:text-[#C89B2C] transition-colors text-sm"
                    data-testid={`footer-nav-${l.href.replace(/[#/]/g, "")}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold text-[#C89B2C] tracking-[0.2em] uppercase mb-5">
              Recursos
            </div>
            <ul className="space-y-3">
              {NAV_LEGAL.map((l) =>
                l.link ? (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      className="text-white/70 hover:text-[#C89B2C] transition-colors text-sm"
                      data-testid={`footer-link-${l.href.replace(/[/]/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ) : (
                  <li key={l.href}>
                    <button
                      onClick={() => scrollTo(l.href)}
                      className="text-white/70 hover:text-[#C89B2C] transition-colors text-sm text-left"
                    >
                      {l.label}
                    </button>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <div className="text-xs font-semibold text-[#C89B2C] tracking-[0.2em] uppercase mb-5">
              Contacto
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <Phone size={16} className="text-[#C89B2C] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <div>
                  <a href={`tel:${CONTACT.phone1Tel}`} className="text-white hover:text-[#C89B2C] transition-colors block">
                    {CONTACT.phone1}
                  </a>
                  <a href={`tel:${CONTACT.phone2Tel}`} className="text-white hover:text-[#C89B2C] transition-colors block">
                    {CONTACT.phone2}
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-[#C89B2C] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <a href={`mailto:${CONTACT.email}`} className="text-white hover:text-[#C89B2C] transition-colors break-all">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex gap-3">
                <MapPin size={16} className="text-[#C89B2C] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <div>
                  <div className="text-white/90">{CONTACT.address}</div>
                  <div className="text-white/60 text-xs">{CONTACT.addressExtra}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col lg:flex-row justify-between items-center gap-4 text-center lg:text-left">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} Great Solutions US. Todos los derechos reservados.
          </div>
          <div className="text-xs text-white/50">
            Yohaly Acevedo · Notary Public FL · IRS Certified · CAA
          </div>
        </div>

        <div className="mt-6 text-[11px] text-white/40 text-center leading-relaxed max-w-4xl mx-auto">
          Great Solutions US no es un despacho jurídico. Los servicios de asistencia con formularios migratorios se
          limitan a la preparación y llenado; no ofrecemos representación ni asesoría legal. Para consultas legales
          consulta a un abogado autorizado.
        </div>
      </div>
    </footer>
  );
}
